import { type SelectHTMLAttributes, type Ref, useId } from 'react';
import { cn } from '../../lib/utils';
import { fieldClass } from './fieldClass';
import { FieldError } from './FieldError';
import { FIELD_LABEL_TYPOGRAPHY } from './FieldLabel';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  /**
   * Renders a decorative required marker (" *") after the label text and sets
   * aria-required on the select. The marker is aria-hidden so it never enters
   * the select's accessible name - a caller toggling this prop in response to
   * some other control (e.g. a sibling checkbox) does not change the label a
   * screen reader announces for THIS field. Callers whose "required" state can
   * change at runtime should pass label as a stable string and vary only this
   * prop, rather than swapping the label text itself.
   *
   * Presentational only - it does not validate anything. The field's actual
   * validation lives in the caller's zod resolver.
   */
  showRequiredMarker?: boolean;
}

export function Select({ ref, className, label, error, id, showRequiredMarker, children, ...props }: SelectProps & { ref?: Ref<HTMLSelectElement> }) {
  const autoId = useId();
  const errorId = `${id ?? autoId}-error`;
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className={`${FIELD_LABEL_TYPOGRAPHY} text-secondary`}>
          {label}
          {showRequiredMarker && (
            <span aria-hidden="true"> *</span>
          )}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          className={cn(
            fieldClass({ error: Boolean(error) }),
            'w-full appearance-none py-2 pl-3 pr-10 text-secondary',
            className,
          )}
          {...props}
          aria-invalid={error ? true : props['aria-invalid']}
          aria-describedby={error ? [errorId, props['aria-describedby']].filter(Boolean).join(' ') : props['aria-describedby']}
          aria-required={showRequiredMarker ? true : props['aria-required']}
        >
          {children}
        </select>
        <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
      <FieldError id={errorId} error={error} />
    </div>
  );
}
